import { SnapshotCard } from '@/components/SnapshotCard'
import { Badge } from '@/components/ui/badge'
import { detectWingetFromPayload, wingetMatchSummary } from '@/lib/wingetDetection'

function SnapshotField({ label, value }) {
  const text = value == null || value === '' ? '—' : String(value)
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{text}</span>
    </div>
  )
}

function YesNoBadge({ value }) {
  if (value !== true && value !== false) {
    return <span className="font-medium">—</span>
  }
  return (
    <Badge variant={value ? 'success' : 'secondary'}>{value ? 'Yes' : 'No'}</Badge>
  )
}

/** Winget availability from an installed-software snapshot (Software tab). */
export function WingetStatusCard({ snapshot }) {
  const winget = detectWingetFromPayload(snapshot?.payload)
  const collectedAt = snapshot?.collected_at ?? null
  const summary = wingetMatchSummary(winget.matches)

  if (!winget.hasData) {
    return (
      <SnapshotCard title="Winget" collectedAt={collectedAt}>
        <p className="text-sm text-muted-foreground">
          No installed-software inventory yet. Deploy the installed-software RMM script
          from Atera to detect whether Windows Package Manager is available on this device.
        </p>
      </SnapshotCard>
    )
  }

  const pill = winget.installed ? 'Available' : 'Not detected'
  const pillVariant = winget.installed ? 'ready' : 'inactive'
  const collapsedSummary = winget.installed
    ? summary || 'Windows Package Manager detected'
    : 'Winget not found in software inventory'

  return (
    <SnapshotCard
      title="Winget"
      collectedAt={collectedAt}
      pill={pill}
      pillVariant={pillVariant}
      collapsedSummary={collapsedSummary}
    >
      <div className="flex justify-between gap-4 text-sm">
        <span className="text-muted-foreground">Winget available</span>
        <YesNoBadge value={winget.installed} />
      </div>
      <SnapshotField label="Matched products" value={summary || '—'} />
      {!winget.installed ? (
        <p className="pt-2 text-xs text-muted-foreground">
          Winget is not reported in the software inventory. Install or update Microsoft App
          Installer if you want package installs from the command line on this device.
        </p>
      ) : null}
      {winget.matches.length > 0 ? (
        <div className="pt-3">
          <p className="mb-2 text-xs font-medium text-muted-foreground">Matching inventory</p>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Product</th>
                  <th className="px-3 py-2 font-medium">Version</th>
                  <th className="px-3 py-2 font-medium">Publisher</th>
                </tr>
              </thead>
              <tbody>
                {winget.matches.map((row, index) => (
                  <tr key={`${row.name}-${index}`} className="border-b border-border last:border-0">
                    <td className="px-3 py-2 font-medium">{row.name || '—'}</td>
                    <td className="px-3 py-2">{row.version || '—'}</td>
                    <td className="px-3 py-2">{row.publisher || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </SnapshotCard>
  )
}
